import React, { ReactNode, useMemo } from "react";
import styled, { css, useTheme } from "styled-components";
import { hexToRGB } from "@shared/utils/misc";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  getNewLookTokens,
  NEW_LOOK_FIGURE_WEIGHT,
  NEW_LOOK_ON_BRAND,
  newLookBrandHero,
  newLookPhoneType,
} from "common/NewLook/tokens.ts";
import {
  NEW_LOOK_PHONE_GUTTER,
  NEW_LOOK_PHONE_HERO_MIN_FONT_SIZE,
} from "common/NewLook/layout.ts";
import {
  formatAmount,
  shownAt,
  splitFraction,
} from "features/portfolio/NewLook/format.ts";
import { phoneCardShadow, PhoneSkeleton } from "./PhoneParts";
import AutoSizeText from "common/Text/AutoSizeText.tsx";

const BRAND_SEGMENT_COLORS = [
  NEW_LOOK_ON_BRAND.text,
  "rgba(255, 255, 255, 0.7)",
  "rgba(255, 255, 255, 0.45)",
  "rgba(255, 255, 255, 0.25)",
];

const SEGMENT_OUTER_EDGES: Record<PhoneSegmentPosition, string> = {
  first: "1px 1px 0",
  middle: "0 1px",
  last: "0 1px 1px",
  only: "1px",
};

/*
 * Building blocks of the phone pages that follow the dashboard (portfolio,
 * trades): a white hero card with a faint blue wash, an area chart with range
 * chips, three figure tiles and allocation rings. Only the dashboard keeps the
 * full-bleed blue header; these pages use the card.
 */

/** Blues from strong to soft, then a grey for "everything else". */
export const PHONE_RING_COLORS = ["#3566D8", "#5E8AE8", "#8AAEF9", "#B5CBFB"];

export const usePhoneRestColor = () =>
  useTheme().isDarkMode ? "rgba(255,255,255,0.12)" : "#E3E7EF";

/**
 * "−$1,234.50" / "+$1,234.50" / "$1,234.50": the user's locale and the
 * display precision, as the desktop figures (formatAmount).
 */
export const phoneMoney = (
  value: number,
  symbol: string,
  precision: number,
  signed = false,
  whole = false
) => {
  // A figure that reads as 0 takes no sign.
  const shown = shownAt(value, whole ? 0 : precision);
  const sign = shown < 0 ? "−" : signed && shown > 0 ? "+" : "";
  return `${sign}${symbol}${formatAmount(
    Math.abs(value),
    whole ? 0 : precision
  )}`;
};

/** For tiles: whole units from 1,000 up, so the figure fits a third of the width. */
export const phoneTileMoney = (
  value: number,
  symbol: string,
  precision: number,
  signed = false
) => phoneMoney(value, symbol, precision, signed, Math.abs(value) >= 1000);

const COMPACT_MONEY_MIN = 10_000;
const compactMoneyFormat = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

export const phoneCompactMoney = (value: number, symbol: string) =>
  Math.abs(value) >= COMPACT_MONEY_MIN
    ? `${value < 0 ? "−" : ""}${symbol}${compactMoneyFormat.format(
        Math.abs(value)
      )}`
    : undefined;

export const phonePercent = (value: number, digits = 2) =>
  `${value > 0 ? "+" : value < 0 ? "−" : ""}${Math.abs(value).toFixed(
    digits
  )}%`;

/** The hero card: a plain panel, like the page's other cards. */
export const PhoneHeroCard = styled.section`
  position: relative;
  margin: 4px 16px 0;
  padding: 14px 0 12px;
  border-radius: ${(p) => p.theme.radii.card}px;
  overflow: hidden;
  background: ${(p) => p.theme.colors.cardBgColor};
  ${phoneCardShadow};
`;

interface PhoneChangeChipProps {
  children: ReactNode;
  /** The sign decides the colour. */
  value: number;
}

export const PhoneChangeChip = ({ children, value }: PhoneChangeChipProps) => (
  <Chip $tone={value > 0 ? "green" : value < 0 ? "red" : "neutral"}>
    {value !== 0 && <span aria-hidden={true}>{value > 0 ? "▲" : "▼"}</span>}
    {children}
  </Chip>
);

export interface PhoneLegendItem {
  key: string;
  color: string;
  name: string;
  percent: number;
}

/** A wrapped row of dot, name and share. */
export const PhoneLegend = ({ items }: { items: PhoneLegendItem[] }) => (
  <Legend>
    {items.map((item) => (
      <LegendItem key={item.key}>
        <Dot style={{ background: item.color }} />
        <LegendName>{item.name}</LegendName>
        <LegendPercent>
          {item.percent > 0 && item.percent < 1
            ? "<1"
            : Math.round(item.percent)}
          %
        </LegendPercent>
      </LegendItem>
    ))}
  </Legend>
);

/**
 * Top shares as ring segments and legend items: the biggest `max` entries
 * and one grey "other" for the rest.
 */
export const getTopShares = (
  entries: { key: string; name: string; value: number; group?: string }[],
  max: number,
  otherName: string,
  restColor: string
) => {
  // One slice per group, by default per key (a pair passes its symbol as the
  // group, as it can have several trades or positions).
  const merged = new Map<
    string,
    { key: string; name: string; value: number }
  >();
  entries.forEach(({ group, ...entry }) => {
    const id = group ?? entry.key;
    const found = merged.get(id);
    if (found) found.value += entry.value;
    else merged.set(id, { ...entry });
  });
  entries = [...merged.values()];
  const total = entries.reduce(
    (sum, entry) => sum + Math.max(0, entry.value),
    0
  );
  if (!total) return { segments: [], legend: [] };
  const sorted = entries
    .filter((entry) => entry.value > 0)
    .sort((a, b) => b.value - a.value);
  const top = sorted.slice(0, max);
  const rest = sorted.slice(max).reduce((sum, entry) => sum + entry.value, 0);
  const segments: [number, string][] = top.map((entry, index) => [
    entry.value / total,
    PHONE_RING_COLORS[index % PHONE_RING_COLORS.length],
  ]);
  const legend: PhoneLegendItem[] = top.map((entry, index) => ({
    key: entry.key,
    name: entry.name,
    color: PHONE_RING_COLORS[index % PHONE_RING_COLORS.length],
    percent: (entry.value / total) * 100,
  }));
  if (rest > 0) {
    segments.push([rest / total, restColor]);
    legend.push({
      key: "__other",
      name: otherName,
      color: restColor,
      percent: (rest / total) * 100,
    });
  }
  return { segments, legend };
};

export type PhoneSegmentPosition = "first" | "middle" | "last" | "only";

export const getPhoneSegment = (
  index: number,
  length: number
): PhoneSegmentPosition => {
  if (length <= 1) return "only";
  if (index === 0) return "first";
  return index === length - 1 ? "last" : "middle";
};

const segmentShape = (
  radiusPx: number
): Record<
  PhoneSegmentPosition,
  { radius: string; clip: string; pad: string }
> => ({
  first: {
    radius: `${radiusPx}px ${radiusPx}px 0 0`,
    clip: "inset(-24px -24px 0 -24px)",
    pad: "6px 14px 0",
  },
  middle: { radius: "0", clip: "inset(0 -24px 0 -24px)", pad: "0 14px" },
  last: {
    radius: `0 0 ${radiusPx}px ${radiusPx}px`,
    clip: "inset(0 -24px -24px -24px)",
    pad: "0 14px 6px",
  },
  only: { radius: `${radiusPx}px`, clip: "none", pad: "6px 14px" },
});

/**
 * One slice of the phone lists' single grouped card. A virtualized list
 * renders its rows apart, so each is a slice of the card: the corners, the
 * padding and the soft shadow (clipped to the card's outer edge) make the
 * slices read as one card.
 */
export const PhoneSegmentCard = styled.div<{ $pos: PhoneSegmentPosition }>`
  margin: 0 16px;
  padding: ${(p) => segmentShape(p.theme.radii.card)[p.$pos].pad};
  border-radius: ${(p) => segmentShape(p.theme.radii.card)[p.$pos].radius};
  clip-path: ${(p) => segmentShape(p.theme.radii.card)[p.$pos].clip};
  background: ${(p) => p.theme.colors.cardBgColor};
  border: 0 solid ${(p) => p.theme.colors.cardBorderColor};
  border-width: ${(p) => SEGMENT_OUTER_EDGES[p.$pos]};
  box-shadow: ${(p) => (p.theme.isDarkMode ? "none" : p.theme.shadows.panel)};
`;

/** A row inside a slice, with a hairline under it unless it ends the card. */
export const PhoneSegmentRow = styled.div<{ $divider: boolean }>`
  padding: 12px 0;
  border-bottom: ${(p) =>
    p.$divider ? `1px solid ${getNewLookTokens(p.theme).hairline}` : "0"};
`;

/** Fades the right edge of a horizontally scrolling chips row. */
export const PhoneChipsFade = styled.div`
  min-width: 0;
  -webkit-mask-image: linear-gradient(
    to right,
    #000 calc(100% - 28px),
    transparent
  );
  mask-image: linear-gradient(to right, #000 calc(100% - 28px), transparent);
`;

/** Page stack under the chips row. */
export const PhoneStack = styled.div`
  padding-bottom: 16px;
`;

/** A row's status line: a dot and its text. */
export const PhoneDot = styled.span<{ $color: string }>`
  display: inline-block;
  flex: none;
  width: 6px;
  height: 6px;
  margin-right: 5px;
  border-radius: 50%;
  vertical-align: 1px;
  background: ${(p) => p.$color};
`;

const Label = styled.div`
  ${newLookPhoneType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const BigValue = styled.div`
  margin-top: 2px;
  /* One line always. */
  ${newLookPhoneType.hero};
  white-space: nowrap;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Fraction = styled.span`
  color: inherit;
  opacity: 0.55;
`;
const toneStyles = {
  green: css`
    color: ${(p) => p.theme.colors.green};
    background: ${(p) =>
      p.theme.isDarkMode ? hexToRGB(p.theme.colors.green, 0.16) : "#E3F6EE"};
  `,
  red: css`
    color: ${(p) => p.theme.colors.red};
    background: ${(p) =>
      p.theme.isDarkMode ? hexToRGB(p.theme.colors.red, 0.16) : "#FDECEC"};
  `,
  neutral: css`
    color: ${(p) => getNewLookTokens(p.theme).secondary};
    background: ${(p) => getNewLookTokens(p.theme).well};
  `,
};
const Chip = styled.span<{ $tone: "green" | "red" | "neutral" }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  > span:first-child {
    font-size: 9px;
  }
  ${(p) => toneStyles[p.$tone]};
`;
const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 14px;
  padding: 0;
`;
const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  font-size: 13px;
  line-height: 18px;
`;
const Dot = styled.span`
  flex: none;
  width: 6px;
  height: 6px;
  border-radius: 50%;
`;
const LegendName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const LegendPercent = styled.span`
  margin-left: auto;
  font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;

const InlineLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  margin-top: 10px;
`;
const InlineItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  max-width: 100%;
  font-size: 12px;
  line-height: 16px;
`;
const InlineName = styled.span`
  min-width: 0;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const InlinePercent = styled.span`
  flex: none;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;

export interface PhoneSplitSegment {
  key: string;
  name: string;
  color: string;
  brandColor?: string;
  /** 0..1 */
  share: number;
}

interface PhoneAllocationCardProps {
  testId?: string;
  label: ReactNode;
  value: string;
  valueColor?: string;
  loading?: boolean;
  /** One quiet line under the figure. */
  meta?: ReactNode;
  segments: PhoneSplitSegment[];
  aside?: ReactNode;
  metaAction?: ReactNode;
  children?: ReactNode;
  brand?: boolean;
  onToggle?: () => void;
}

/**
 * The compact hero of the portfolio and trades pages: the page's figure, a
 * line of context, one stacked bar split by what makes it up, and its legend.
 */
export const PhoneAllocationCard = ({
  testId,
  label,
  value,
  valueColor,
  loading,
  meta,
  segments,
  aside,
  metaAction,
  children,
  brand,
  onToggle,
}: PhoneAllocationCardProps) => {
  const [whole, fraction] = splitFraction(value);
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const restColor = usePhoneRestColor();
  const shown = useMemo(
    () => foldSegments(segments, t("labels.other"), restColor),
    [segments, t, restColor]
  );
  return (
    <AllocationHero data-testid={testId} $brand={brand}>
      <Inner onClick={onToggle} $clickable={!!onToggle}>
        <TopRow>
          <TopText>
            <Label>{label}</Label>
            {loading ? (
              <span aria-busy="true">
                <PhoneSkeleton
                  $width={170}
                  $height={28}
                  $radius={8}
                  style={{ margin: "3px 0" }}
                />
              </span>
            ) : (
              <BigValue
                style={valueColor && !brand ? { color: valueColor } : undefined}
              >
                <AutoSizeText
                  inheritFont={true}
                  minFontSizePx={NEW_LOOK_PHONE_HERO_MIN_FONT_SIZE}
                >
                  {whole}
                  {fraction && <Fraction>{fraction}</Fraction>}
                </AutoSizeText>
              </BigValue>
            )}
          </TopText>
          {aside}
        </TopRow>
        {(meta || metaAction) && !loading && (
          <MetaRow>
            <Meta>{meta}</Meta>
            {metaAction}
          </MetaRow>
        )}
        {loading ? (
          <PhoneSkeleton
            $width={9999}
            $height={10}
            $radius={5}
            style={{ marginTop: 14 }}
          />
        ) : (
          segments.length > 0 && (
            <>
              <Bar role="img" aria-label={shown.map((s) => s.name).join(", ")}>
                {shown.map((segment, index) => (
                  <span
                    key={segment.key}
                    style={{
                      flexGrow: segment.share,
                      background: getSegmentColor(segment, index, brand),
                    }}
                  />
                ))}
              </Bar>
              {/* One compact line under the bar (as on the account cards):
                  a full legend list cost a lot of room on a phone. */}
              <InlineLegend>
                {shown.map((segment, index) => (
                  <InlineItem key={segment.key} title={segment.name}>
                    <Dot
                      style={{
                        background: getSegmentColor(segment, index, brand),
                      }}
                    />
                    <InlineName>{segment.name}</InlineName>
                    <InlinePercent>
                      {formatShare(segment.share * 100)}
                    </InlinePercent>
                  </InlineItem>
                ))}
              </InlineLegend>
            </>
          )
        )}
      </Inner>
      {children}
    </AllocationHero>
  );
};

const AllocationHero = styled(PhoneHeroCard)<{ $brand?: boolean }>`
  margin-top: 16px;
  ${(p) => p.$brand && brandAllocation};
`;

const getSegmentColor = (
  segment: PhoneSplitSegment,
  index: number,
  brand?: boolean
) =>
  brand
    ? segment.brandColor ??
      BRAND_SEGMENT_COLORS[Math.min(index, BRAND_SEGMENT_COLORS.length - 1)]
    : segment.color;

/** The three biggest shares by name, the rest as one grey "Other". */
const foldSegments = (
  segments: PhoneSplitSegment[],
  otherName: string,
  restColor: string
): PhoneSplitSegment[] => {
  if (segments.length <= 4) return segments;
  const rest = segments.slice(3);
  return [
    ...segments.slice(0, 3),
    {
      key: "__other",
      name: otherName,
      color: restColor,
      share: rest.reduce((sum, segment) => sum + segment.share, 0),
    },
  ];
};

const formatShare = (percent: number) =>
  `${percent > 0 && percent < 1 ? "<1" : Math.round(percent)}%`;

const Inner = styled.div<{ $clickable?: boolean }>`
  padding: 0 ${NEW_LOOK_PHONE_GUTTER}px;
  cursor: ${(p) => (p.$clickable ? "pointer" : "auto")};
  -webkit-tap-highlight-color: transparent;
`;
const TopRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
`;
const TopText = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;
const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 2px;
`;
const Meta = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  ${newLookPhoneType.label};
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Bar = styled.div`
  display: flex;
  gap: 2px;
  height: 10px;
  margin-top: 12px;
  border-radius: 3px;
  overflow: hidden;
  > span {
    min-width: 3px;
  }
`;

/** Segments from the top shares: names, colours and shares for the bar. */
export const toSplitSegments = (shares: {
  legend: PhoneLegendItem[];
}): PhoneSplitSegment[] =>
  shares.legend.map((item) => ({
    key: item.key,
    name: item.name,
    color: item.color,
    share: item.percent / 100,
  }));

interface PhoneRingProps {
  segments: [number, string][];
  size?: number;
  stroke?: number;
}

/** A donut of shares (each 0..1, in order). */
export const PhoneRing = ({
  segments,
  size = 38,
  stroke = 5,
}: PhoneRingProps) => {
  const radius = size / 2 - stroke / 2 - 1;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <svg width={size} height={size} aria-hidden={true} style={{ flex: "none" }}>
      {segments.map(([part, color], index) => {
        const circle = (
          <circle
            key={index}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeDasharray={`${Math.max(
              0,
              circumference * part - (segments.length > 1 ? 2 : 0)
            )} ${circumference}`}
            strokeDashoffset={-circumference * offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        );
        offset += part;
        return circle;
      })}
    </svg>
  );
};
const brandAllocation = css`
  ${newLookBrandHero};
  ${Label}, ${InlineName} {
    color: ${NEW_LOOK_ON_BRAND.secondary};
  }
  ${BigValue} {
    color: ${NEW_LOOK_ON_BRAND.text};
  }
  ${Meta}, ${InlinePercent} {
    color: ${NEW_LOOK_ON_BRAND.muted};
  }
  ${PhoneSkeleton} {
    background: ${NEW_LOOK_ON_BRAND.well};
  }
`;
